import { staffOfferLink } from 'core/helpers/bridge-link-opened';

/**
 * Normalizes the two Meilisearch result sets behind the header's global search bar — the `users`
 * index (Cognito member records) and the `offer-history` index — into one row shape the dropdown
 * can render as a single ranked list.
 *
 * Why both: the bar advertises "account ID, name, or address", but it only ever queried `users`,
 * so a prospect who has an offer and no account came back empty while the Search page's Offers tab
 * found them with the same term (#35516). Because the two indexes now share one list, each row
 * carries a `resultType` the dropdown prints in its leading column — otherwise an agent can't tell
 * which of the two things they're about to open.
 *
 * Members are listed ahead of offers deliberately: a member record is the richer destination (it
 * reaches the account's policies, billing, and documents), and when the member doesn't exist —
 * exactly the case this fixes — there are no member rows to push the offers down.
 */

export const MEMBER_RESULT_TYPE = 'Member';
export const OFFER_RESULT_TYPE = 'Offer';

// Street and city are comma-separated; state and zip read as one token ("IL 61604"). Every piece
// is optional, and a missing one is dropped rather than leaving a dangling ", , " in the cell —
// offer and member documents both predate parts of their address shape.
const formatAddress = ({ address, city, state, zip } = {}) => {
  const stateAndZip = [state, zip].filter(Boolean).join(' ');
  return [address, city, stateAndZip].filter(Boolean).join(', ');
};

const toMemberRow = (hit) => {
  const attributes = hit?.attributes || {};
  const accountId = hit?.Username || '';

  return {
    key: `member-${accountId}`,
    resultType: MEMBER_RESULT_TYPE,
    id: accountId,
    fname: attributes.name || '',
    lname: attributes.family_name || '',
    address: formatAddress({
      address: attributes.address,
      city: attributes.custom_city,
      state: attributes.custom_state,
      zip: attributes.custom_zip
    }),
    city: attributes.custom_city || '',
    state: attributes.custom_state || '',
    zip: attributes.custom_zip || '',
    // A member's date is when the account was created, matching the Members tab.
    dateValue: hit?.UserCreateDate || null,
    to: `/customer/${accountId}`
  };
};

const toOfferRow = (hit) => {
  const quote = hit?.quote || {};
  const submittedAddress = quote.submittedAddress || {};
  const offerId = hit?.id || '';

  return {
    key: `offer-${offerId}`,
    resultType: OFFER_RESULT_TYPE,
    // An offer has no account ID — its member may not exist yet. `webUserId` is the identifier
    // that ties an offer's revisions together, so it is what belongs in the ID column. Synthetic
    // farm renewal offers are minted without one (offer-record.mapper.ts), so those fall back to
    // the offer id, which for exactly that class begins with the policy number.
    id: hit?.webUserId || offerId,
    fname: quote.fname || '',
    lname: quote.lname || '',
    address: formatAddress(submittedAddress),
    city: submittedAddress.city || '',
    state: submittedAddress.state || '',
    zip: submittedAddress.zip || '',
    // An offer's date is its last edit, matching the Offers tab's "Last Update" column.
    dateValue: hit?.updatedDateTime || null,
    to: staffOfferLink(offerId)
  };
};

export const buildGlobalSearchHits = ({ customerHits, offerHits } = {}) => [
  ...(customerHits || []).map(toMemberRow),
  ...(offerHits || []).map(toOfferRow)
];
